import {colors, radii, tokens} from '@commercial-helios/ui/theme'
import {m as motion} from 'framer-motion'
import React from 'react'
import styled, {createGlobalStyle} from 'styled-components'

import type {ExpenseReportGeneratorQuery} from '@/graphql-types'
import {useGlobalStrings} from '@helios/shared/providers/global-strings'
import {Text} from '@helios/shared/styles/typography'
import type ImageSource from '@helios/shared/types/image'
import type {PrismicData} from '@helios/shared/types/prismic-data'
import mq from '@helios/shared/utils/media-query'

interface Props {
    loadingImage: ImageSource
    content: PrismicData<ExpenseReportGeneratorQuery, 'allPrismicExpenseReportGenerator'>['data']
}

const variants = {
    initial: {
        opacity: 0,
        y: 20,
    },
    animate: {
        opacity: 1,
        y: 0,
    },
    exit: {
        opacity: 0,
    },
}

const Loading = ({loadingImage, content}: Props) => {
    const [activeIndex, setActiveIndex] = React.useState(0)
    const globalStrings = useGlobalStrings()

    React.useEffect(() => {
        let timeout: ReturnType<typeof setTimeout>

        timeout = setTimeout(() => {
            setActiveIndex((activeIndex) => (activeIndex + 1) % content.loadingFacts.length)
        }, 3000)

        return () => {
            clearTimeout(timeout)
        }
    }, [activeIndex, content.loadingFacts.length])

    return (
        <OuterWrapper>
            <PageStyles />

            {/* substring of the url to get rid of the parameters that broke the animated PNG */}
            <LoadingImage
                src={loadingImage.url.substring(0, loadingImage.url.indexOf('?'))}
                alt={loadingImage.alt ?? globalStrings.loading}
            />
            <TextWrapper>
                <Text>{content.reportGenerating}</Text>

                <Box>
                    <Text variant="paragraph-base" color="lightMuted">
                        {content.didYouKnow}
                    </Text>

                    <Text
                        as={motion.p}
                        variant="title-base"
                        color="muted"
                        variants={variants}
                        initial="initial"
                        animate="animate"
                        exit="exit"
                        key={activeIndex}
                    >
                        {content.loadingFacts[activeIndex].fact}
                    </Text>
                </Box>
            </TextWrapper>
        </OuterWrapper>
    )
}

const PageStyles = createGlobalStyle`
    body {
        background: ${colors.background.pink};
    }
`

const OuterWrapper = styled.div`
    display: grid;
    text-align: center;
    row-gap: ${tokens.spacing20};
`

const LoadingImage = styled.img`
    justify-self: center;
    height: 120px;

    ${mq.min('sm')} {
        height: 200px;
    }
`

const TextWrapper = styled.div`
    display: grid;
    row-gap: ${tokens.spacing40};

    ${mq.min('sm')} {
        row-gap: ${tokens.spacing60};
    }
`

const Box = styled.div`
    display: grid;
    width: 100%;
    max-width: 700px;
    padding: ${tokens.spacing20};
    margin: 0 auto;
    background: ${colors.background.default};
    border-radius: ${radii.arc8};
    row-gap: ${tokens.spacing10};

    ${mq.min('sm')} {
        border-radius: ${radii.arc20};
    }
`

export default Loading
